<template>
  <HrPageLayout
    title="Confirmation & probation"
    subtitle="Employees on probation — confirm, extend, filter and export"
    icon="✅"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Public Service Board' },
      { label: 'Confirmation & probation' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary hr-btn-sm" @click="loadData">Refresh</button>
      <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" :disabled="!filteredRows.length" @click="exportCsv">CSV</button>
      <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" :disabled="!filteredRows.length" @click="exportPdf">PDF</button>
    </template>

    <div class="cp-metrics">
      <div class="m"><span>Total</span><strong>{{ rows.length }}</strong></div>
      <div class="m warn"><span>Alert (&le; 1 month)</span><strong>{{ alerts.length }}</strong></div>
      <div class="m"><span>Showing</span><strong>{{ filteredRows.length }}</strong></div>
    </div>

    <div class="table-card">
      <div class="filters">
        <input v-model="filters.search" class="form-control" placeholder="Search name, PF no, department…" />
        <select v-model="filters.status" class="form-select">
          <option value="">All statuses</option>
          <option value="On Probation">On Probation</option>
          <option value="Confirmed">Confirmed</option>
          <option value="Alert">Alert ≤1 month</option>
          <option value="Overdue">Overdue</option>
        </select>
        <input v-model="filters.dateFrom" type="date" class="form-control" title="End date from" />
        <input v-model="filters.dateTo" type="date" class="form-control" title="End date to" />
        <button type="button" class="hr-btn hr-btn-secondary hr-btn-sm" @click="resetFilters">Clear</button>
      </div>

      <div v-if="alerts.length" class="alert alert-warning py-2">
        <strong>Alert:</strong> {{ alerts.length }} employee(s) have one month or less remaining before probation ends.
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Remaining</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="8" class="text-muted">Loading...</td></tr>
            <tr v-else-if="!filteredRows.length"><td colspan="8" class="text-muted">No matching probation records.</td></tr>
            <tr v-for="r in pagedRows" :key="'pb-' + r.employee_id">
              <td>
                <strong>{{ r.employee_name }}</strong><br>
                <small class="text-muted">{{ r.empNo || "—" }}</small>
              </td>
              <td>{{ r.departmentName || "—" }}</td>
              <td>{{ r.probation_start || "—" }}</td>
              <td>{{ r.probation_end || "—" }}</td>
              <td><span class="badge-status" :class="statusTone(r)">{{ r.status }}</span></td>
              <td style="min-width: 160px;">
                <div class="progress" style="height: 14px;">
                  <div class="progress-bar" :style="{ width: `${Math.max(0, Math.min(100, Number(r.progress_percent || 0)))}%` }">
                    {{ Math.max(0, Math.min(100, Number(r.progress_percent || 0))) }}%
                  </div>
                </div>
              </td>
              <td>
                <span :class="r.alert_one_month ? 'text-danger fw-bold' : ''">
                  {{ remainingLabel(r.remaining_days) }}
                </span>
              </td>
              <td class="text-center actions">
                <button
                  v-if="canManage && r.status !== 'Confirmed'"
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-success"
                  @click="confirmRow(r)"
                >Confirm</button>
                <button
                  v-if="canManage && r.status !== 'Confirmed'"
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-outline-primary"
                  @click="openExtend(r)"
                >Extend</button>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="viewRow(r)">View</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pager" v-if="totalPages > 1">
        <button type="button" class="hr-btn hr-btn-sm" :disabled="page<=1" @click="page--">Prev</button>
        <span>Page {{ page }} / {{ totalPages }}</span>
        <button type="button" class="hr-btn hr-btn-sm" :disabled="page>=totalPages" @click="page++">Next</button>
      </div>
    </div>

    <div v-if="showExtendModal" class="modal-overlay" @click.self="showExtendModal = false">
      <div class="modal-card">
        <h5 class="mb-3">Extend probation</h5>
        <div class="form-group">
          <label>Employee</label>
          <input type="text" class="form-control" :value="selectedRow ? selectedRow.employee_name : ''" disabled>
        </div>
        <div class="form-group">
          <label>Extend by (months)</label>
          <input v-model.number="extendMonths" type="number" min="1" max="24" class="form-control">
        </div>
        <div class="form-group">
          <label>Notes</label>
          <textarea v-model="extendNotes" rows="3" class="form-control"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showExtendModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitExtend">
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showViewModal" class="modal-overlay" @click.self="showViewModal = false">
      <div class="modal-card">
        <h5>Employee probation detail</h5>
        <dl v-if="selectedRow" class="detail-dl">
          <div><dt>Name</dt><dd>{{ selectedRow.employee_name }}</dd></div>
          <div><dt>PF No</dt><dd>{{ selectedRow.empNo || '—' }}</dd></div>
          <div><dt>Department</dt><dd>{{ selectedRow.departmentName || '—' }}</dd></div>
          <div><dt>Probation start</dt><dd>{{ selectedRow.probation_start || '—' }}</dd></div>
          <div><dt>Probation end</dt><dd>{{ selectedRow.probation_end || '—' }}</dd></div>
          <div><dt>Status</dt><dd>{{ selectedRow.status }}</dd></div>
          <div><dt>Remaining</dt><dd>{{ remainingLabel(selectedRow.remaining_days) }}</dd></div>
        </dl>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showViewModal = false">Close</button>
          <button v-if="canManage && selectedRow && selectedRow.status !== 'Confirmed'" type="button" class="hr-btn hr-btn-success" @click="confirmRow(selectedRow); showViewModal=false">Confirm</button>
          <button v-if="canManage && selectedRow && selectedRow.status !== 'Confirmed'" type="button" class="hr-btn hr-btn-primary" @click="showViewModal=false; openExtend(selectedRow)">Extend</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight, toast } from "@/api";
import { downloadCsv, downloadCountyTablePdf } from "@/utils/countyReportExport";

export default {
  name: "ModuleConfirmationProbation",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      alerts: [],
      canManage: false,
      showExtendModal: false,
      showViewModal: false,
      selectedRow: null,
      extendMonths: 1,
      extendNotes: "",
      page: 1,
      pageSize: 15,
      filters: { search: "", status: "", dateFrom: "", dateTo: "" }
    };
  },
  computed: {
    filteredRows() {
      const q = String(this.filters.search || "").toLowerCase().trim();
      const st = this.filters.status;
      const from = this.filters.dateFrom;
      const to = this.filters.dateTo;
      return (this.rows || []).filter((r) => {
        if (q) {
          const hay = `${r.employee_name || ""} ${r.empNo || ""} ${r.departmentName || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (st === "Alert" && !r.alert_one_month) return false;
        if (st === "Overdue" && Number(r.remaining_days || 0) >= 0) return false;
        if (st && st !== "Alert" && st !== "Overdue" && String(r.status || "") !== st) return false;
        const end = r.probation_end || r.end_date || "";
        if (from && end && end < from) return false;
        if (to && end && end > to) return false;
        return true;
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pagedRows() {
      const p = Math.min(this.page, this.totalPages);
      const start = (p - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() { this.page = 1; }
    }
  },
  mounted() {
    this.canManage = !!getRight("CONFIRMATION_PROBATION_MANAGE") || !!getRight("CONFIRMATION_PROBATION_VIEW");
    this.loadData();
  },
  methods: {
    resetFilters() {
      this.filters = { search: "", status: "", dateFrom: "", dateTo: "" };
    },
    remainingLabel(days) {
      const d = Number(days || 0);
      if (d < 0) return `${Math.abs(d)} day(s) overdue`;
      return `${d} day(s) left`;
    },
    statusTone(r) {
      if (r.status === "Confirmed") return "ok";
      if (r.alert_one_month || Number(r.remaining_days || 0) < 0) return "bad";
      return "warn";
    },
    async loadData() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "getProbationEmployeesDashboard");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          this.rows = res?.data?.data?.data || [];
          this.alerts = res?.data?.data?.alerts || [];
          this.canManage = !!(res?.data?.data?.permissions?.manage || getRight("CONFIRMATION_PROBATION_MANAGE"));
        } else {
          this.rows = [];
          this.alerts = [];
          toast.error(res?.data?.message || "Failed to load probation data");
        }
      } catch (e) {
        this.rows = [];
        this.alerts = [];
        toast.error("Failed to load probation data");
      } finally {
        this.loading = false;
      }
    },
    viewRow(r) {
      this.selectedRow = r;
      this.showViewModal = true;
    },
    exportCsv() {
      downloadCsv(
        ["Employee", "Emp No", "Department", "Start", "End", "Status", "Remaining"],
        this.filteredRows.map((r) => [
          r.employee_name || "",
          r.empNo || "",
          r.department_name || r.departmentName || "",
          r.probation_start || r.start_date || "",
          r.probation_end || r.end_date || "",
          r.status || "",
          this.remainingLabel(r.remaining_days),
        ]),
        `confirmations_${Date.now()}.csv`
      );
      toast.success("CSV downloaded");
    },
    async exportPdf() {
      try {
        await downloadCountyTablePdf({
          title: "CONFIRMATION & PROBATION REPORT",
          subtitle: "Public Service Board",
          periodLabel: new Date().toLocaleString("en-KE"),
          headers: ["Employee", "PF No", "Department", "Start", "End", "Status", "Remaining"],
          rows: this.filteredRows.map((r) => [
            r.employee_name || "",
            r.empNo || "",
            r.department_name || r.departmentName || "",
            r.probation_start || r.start_date || "",
            r.probation_end || r.end_date || "",
            r.status || "",
            this.remainingLabel(r.remaining_days),
          ]),
          filename: `confirmations_${Date.now()}.pdf`,
          orientation: "landscape"
        });
        toast.success("PDF downloaded");
      } catch (e) {
        toast.error(e?.message || "PDF failed");
      }
    },
    async confirmRow(r) {
      if (!confirm(`Confirm employment for ${r.employee_name}?`)) return;
      const fd = new FormData();
      fd.append("function", "confirmProbationEmployee");
      fd.append("employeeId", r.employee_id);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Employee confirmed");
        this.loadData();
      } else toast.error(res?.data?.message || "Confirm failed");
    },
    openExtend(r) {
      this.selectedRow = r;
      this.extendMonths = 1;
      this.extendNotes = "";
      this.showExtendModal = true;
    },
    async submitExtend() {
      if (!this.selectedRow) return;
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "extendProbationEmployee");
        fd.append("employeeId", this.selectedRow.employee_id);
        fd.append("months", String(this.extendMonths));
        fd.append("notes", this.extendNotes || "");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Probation extended");
          this.showExtendModal = false;
          this.loadData();
        } else toast.error(res?.data?.message || "Extend failed");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.cp-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
.cp-metrics .m { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
.cp-metrics span { display: block; font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; }
.cp-metrics strong { font-size: 22px; color: #0c2d48; }
.cp-metrics .warn strong { color: #c2410c; }
.table-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px; }
.filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.filters .form-control, .filters .form-select { max-width: 220px; }
.actions { white-space: nowrap; }
.actions .hr-btn { margin: 2px; }
.badge-status { font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: #f1f5f9; }
.badge-status.ok { background: #ecfdf5; color: #047857; }
.badge-status.warn { background: #fff7ed; color: #c2410c; }
.badge-status.bad { background: #fef2f2; color: #b91c1c; }
.pager { display: flex; justify-content: center; gap: 12px; align-items: center; margin-top: 12px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.45); display: flex; align-items: center; justify-content: center; z-index: 10060; }
.modal-card { background: #fff; border-radius: 14px; padding: 20px; width: min(440px, 94vw); }
.form-group { margin-bottom: 10px; }
.form-group label { font-weight: 600; font-size: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.detail-dl { display: grid; gap: 8px; }
.detail-dl div { display: grid; grid-template-columns: 120px 1fr; font-size: 13px; }
.detail-dl dt { color: #64748b; margin: 0; }
.detail-dl dd { margin: 0; }
</style>
